<template>
    <settings-page :title="title" :description="description">
        <div class="animations">
            <div class="animations__container" :style="containerStyle">
                <section class="animations__pane">
                    <animation-library
                        :animations="animations"
                        @select="onSelect"
                        @add="onAdd"
                        @delete="onDelete"
                    />
                </section>

                <section class="animations__pane">
                    <animation-editor
                        :animation="selectedAnimation"
                        @input="onInput"
                        @back="setActivePane('library')"
                    />
                </section>
            </div>
        </div>
    </settings-page>
</template>

<script>
import uniqid from 'uniqid';
import { mapMutations, mapState } from 'vuex';

import SettingsPage from '@/components/settings/shared/settings-page';
import AnimationLibrary from '@/components/settings/component/animations/animation-library';
import AnimationEditor from '@/components/settings/component/animations/animation-editor';
import { SHOW_PROMPT } from '@/store/types/mutation-types';
import { Animation, Tween, TweenVar } from '@/data/data-models';

export default {
    name: 'settings-animations',
    components: {
        SettingsPage,
        AnimationLibrary,
        AnimationEditor,
    },

    data() {
        return {
            activePane: 'library',
            animations: null,
            title: 'Animation Studio',
            description: 'This is where you can design, preview, and save various animated effects.',
            selectedAnimation: null,
        };
    },

    computed: {
        ...mapState({
            settings: state => state.settings,
        }),

        containerStyle() {
            return {
                left: this.activePane === 'editor' ? '-100%' : '0%',
            };
        },
    },

    watch: {
        settings: {
            handler() {
                this.animations = this.settings.animations || [];
            },
            immediate: true,
        },
    },

    methods: {
        ...mapMutations({
            showPrompt: SHOW_PROMPT,
        }),

        onSelect(id) {
            this.selectedAnimation = this.animations.find(a => a.id === id);
            this.setActivePane('editor');
        },

        onAdd() {
            const tweenVar = new TweenVar({
                id: uniqid(),
            });

            const tween = new Tween({
                id: uniqid(),
                vars: [tweenVar],
            });

            const animation = new Animation({
                id: uniqid(),
                tweens: [tween],
            });

            this.selectedAnimation = animation;
            this.setActivePane('editor');

            // waiting for the next pane to slide into view, before adding the new animation effect
            // otherwise the animation list will "jump" during the sliding animation
            window.setTimeout(() => this.animations.push(animation), 500);
        },

        onInput(animation) {
            this.selectedAnimation = animation;
            this.$emit('input', 'animations', this.animations);
        },

        onDelete(id) {
            this.showPrompt({
                title: 'Confirm',
                description: 'Are you sure you want to delete this animation?',
                onContinue: () => this.deleteAnimation(id),
            });
        },

        deleteAnimation(id) {
            const index = this.animations.findIndex(a => a.id === id);

            this.animations.splice(index, 1);
            this.onInput();
        },

        setActivePane(pane) {
            this.activePane = pane;
            window.scrollTo(0, 0);
        },
    },
};
</script>

<style lang="scss" scoped>
.animations {
    overflow: hidden;

    &__container {
        position: relative;
        display: flex;
        transition: left 0.25s;
    }

    &__pane {
        min-width: 100%;
    }
}
</style>
